import type { TenantDomain } from '@erp/shared';
import Decimal from 'decimal.js';
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  Building2,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCircle2,
  ClipboardList,
  Coins,
  Copy,
  CreditCard,
  Edit3,
  ExternalLink,
  Globe,
  KeyRound,
  PauseCircle,
  Play,
  Plus,
  ReceiptText,
  RefreshCw,
  Trash2,
  UserCog,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Button as ShButton,
  Separator,
  Skeleton,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog, FormField } from '../../components/ui/index.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/intl.js';
import { TOGGLEABLE_MODULES, type ModuleKey } from '../../lib/modules.js';
import { narrowUsd } from '../../lib/money.js';
import { platformApi } from '../../lib/platformApi.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

import { AuditDiff } from './_shared/AuditDiff.js';
import { LogoUpload } from './LogoUpload.js';
import { TenantLogo } from './TenantLogo.js';

type BillingStatus = 'trialing' | 'active' | 'past_due' | 'unpaid' | 'cancelled';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'closed';
  plan: 'free' | 'starter' | 'pro' | 'enterprise';
  defaultLocale: 'en' | 'ar';
  baseCurrencyCode: string;
  logoUrl: string | null;
  billingStatus: BillingStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  lastPaymentAt: string | null;
  yearlyPriceAmount: string | null;
  yearlyPriceCurrencyCode: string | null;
  billingNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DetailResponse {
  tenant: Tenant;
  stats: { userCount: number; planCount: number; revenue30d: number };
}

interface AuditEntry {
  id: string;
  platformAdminId: string;
  action: string;
  targetTenantId: string | null;
  diff: unknown;
  createdAt: string;
}

interface ImpersonateResponse {
  tokens: { accessToken: string; expiresIn: number };
  expiresAt: string;
  tenantId: string;
  impersonatedUser: { id: string; email: string; fullName: string };
}

const STATUS_VARIANT: Record<Tenant['status'], 'success' | 'warning' | 'secondary'> = {
  active: 'success',
  suspended: 'warning',
  closed: 'secondary',
};

const PLAN_VARIANT: Record<Tenant['plan'], 'secondary' | 'info' | 'success' | 'default'> = {
  free: 'secondary',
  starter: 'info',
  pro: 'success',
  enterprise: 'default',
};

const BILLING_VARIANT: Record<BillingStatus, 'info' | 'success' | 'warning' | 'danger' | 'secondary'> = {
  trialing: 'info',
  active: 'success',
  past_due: 'warning',
  unpaid: 'danger',
  cancelled: 'secondary',
};

export function PlatformTenantDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('platform');
  const navigate = useNavigate();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [impersonateReason, setImpersonateReason] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    plan: 'free' as Tenant['plan'],
    defaultLocale: 'en' as Tenant['defaultLocale'],
    baseCurrencyCode: 'USD',
    logoUrl: '',
  });
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingForm, setBillingForm] = useState({
    billingStatus: 'trialing' as BillingStatus,
    yearlyPriceAmount: '',
    yearlyPriceCurrencyCode: 'USD',
    currentPeriodEnd: '',
    trialEndsAt: '',
    billingNotes: '',
  });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ years: 1, note: '' });
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPwInput, setResetPwInput] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetCopied, setResetCopied] = useState(false);
  const [resetResult, setResetResult] = useState<{ email: string; tempPassword: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [d, a] = await Promise.all([
        platformApi().get<DetailResponse>(`/tenants/${id}`),
        platformApi().get<{ items: AuditEntry[] }>('/audit', {
          params: { tenantId: id, limit: 10 },
        }),
      ]);
      setData(d.data);
      setAudit(a.data.items);
      setEditForm({
        name: d.data.tenant.name,
        plan: d.data.tenant.plan,
        defaultLocale: d.data.tenant.defaultLocale,
        baseCurrencyCode: d.data.tenant.baseCurrencyCode,
        logoUrl: d.data.tenant.logoUrl ?? '',
      });
      setBillingForm({
        billingStatus: d.data.tenant.billingStatus,
        yearlyPriceAmount: d.data.tenant.yearlyPriceAmount ?? '',
        yearlyPriceCurrencyCode:
          d.data.tenant.yearlyPriceCurrencyCode ?? d.data.tenant.baseCurrencyCode,
        currentPeriodEnd: d.data.tenant.currentPeriodEnd
          ? d.data.tenant.currentPeriodEnd.slice(0, 10)
          : '',
        trialEndsAt: d.data.tenant.trialEndsAt ? d.data.tenant.trialEndsAt.slice(0, 10) : '',
        billingNotes: d.data.tenant.billingNotes ?? '',
      });
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const fmtMoney = useMemo(() => {
    const cur = data?.tenant.baseCurrencyCode ?? 'USD';
    return (n: number) => {
      try {
        return narrowUsd(formatNumber(n, { locale: i18n.language }, {
          style: 'currency',
          currency: cur,
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 0,
        }));
      } catch {
        return `${n} ${cur}`;
      }
    };
  }, [data?.tenant.baseCurrencyCode, i18n.language]);

  if (!data) {
    return (
      <div className="flex flex-col gap-4">
        {error && (
          <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
        <Card className="p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-4 h-32 w-full" />
        </Card>
      </div>
    );
  }

  const tenant = data.tenant;

  const saveEdit = async () => {
    setBusy(true);
    try {
      await platformApi().patch(`/tenants/${tenant.id}`, {
        ...editForm,
        logoUrl: editForm.logoUrl.trim() ? editForm.logoUrl.trim() : null,
      });
      setEditOpen(false);
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const saveBilling = async () => {
    setBusy(true);
    try {
      await platformApi().patch(`/tenants/${tenant.id}`, {
        billingStatus: billingForm.billingStatus,
        yearlyPriceAmount: billingForm.yearlyPriceAmount.trim() || null,
        yearlyPriceCurrencyCode: billingForm.yearlyPriceCurrencyCode || null,
        currentPeriodEnd: billingForm.currentPeriodEnd
          ? new Date(`${billingForm.currentPeriodEnd}T23:59:59Z`).toISOString()
          : null,
        trialEndsAt: billingForm.trialEndsAt
          ? new Date(`${billingForm.trialEndsAt}T23:59:59Z`).toISOString()
          : null,
        billingNotes: billingForm.billingNotes.trim() || null,
      });
      setBillingOpen(false);
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = async () => {
    setBusy(true);
    try {
      await platformApi().post(`/tenants/${tenant.id}/record-payment`, {
        years: paymentForm.years,
        note: paymentForm.note.trim() || undefined,
      });
      setPaymentOpen(false);
      setPaymentForm({ years: 1, note: '' });
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const doResetPassword = async () => {
    setBusy(true);
    setResetError(null);
    try {
      const body = resetPwInput.trim() ? { password: resetPwInput.trim() } : {};
      const r = await platformApi().post<{ email: string; tempPassword: string }>(
        `/tenants/${tenant.id}/reset-owner-password`,
        body,
      );
      setResetResult(r.data);
      setResetPwInput('');
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status;
      setResetError(
        status === 422 || status === 400
          ? t('detail.resetPw.weak', {
              defaultValue:
                'Password must be at least 10 characters with an uppercase letter, a lowercase letter, and a digit.',
            })
          : t('detail.resetPw.error', { defaultValue: 'Could not reset the password.' }),
      );
    } finally {
      setBusy(false);
    }
  };

  const doSuspend = async () => {
    setBusy(true);
    try {
      await platformApi().post(`/tenants/${tenant.id}/suspend`, {
        reason: suspendReason || undefined,
      });
      setSuspendOpen(false);
      setSuspendReason('');
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const doReactivate = async () => {
    setBusy(true);
    try {
      await platformApi().post(`/tenants/${tenant.id}/reactivate`, {});
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const doImpersonate = async () => {
    if (!impersonateReason.trim()) return;
    setBusy(true);
    try {
      const r = await platformApi().post<ImpersonateResponse>(
        `/tenants/${tenant.id}/impersonate`,
        { reason: impersonateReason.trim() },
      );
      // Drop the minted token into the TENANT auth store so the next
      // navigation lands inside the tenant app as the owner user.
      const { useAuthStore } = await import('../../lib/authStore.js');
      useAuthStore.getState().setSession({
        accessToken: r.data.tokens.accessToken,
        expiresIn: r.data.tokens.expiresIn,
        user: {
          id: r.data.impersonatedUser.id,
          tenantId: r.data.tenantId,
          email: r.data.impersonatedUser.email,
          fullName: r.data.impersonatedUser.fullName,
          locale: tenant.defaultLocale,
          status: 'active',
        },
        tenant: {
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          defaultLocale: tenant.defaultLocale,
          baseCurrencyCode: tenant.baseCurrencyCode,
        },
      });
      setImpersonateOpen(false);
      setImpersonateReason('');
      navigate('/dashboard');
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/platform/tenants"
          className="inline-flex items-center gap-1 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline"
        >
          <ArrowLeft className="h-3 w-3 rtl:rotate-180" aria-hidden />
          {t('tenants.title')}
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <TenantLogo
            logoUrl={tenant.logoUrl}
            imgClassName="h-12 w-12 rounded-md object-cover ring-1 ring-border"
            fallback={
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-warning/15 text-warning">
                <Building2 className="h-6 w-6" aria-hidden />
              </div>
            }
          />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">{tenant.name}</h1>
              <Badge variant={STATUS_VARIANT[tenant.status]}>
                {t(`tenants.status.${tenant.status}`)}
              </Badge>
              <Badge variant={PLAN_VARIANT[tenant.plan]} className="capitalize">
                {tenant.plan}
              </Badge>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-fg-muted">
              <span className="font-mono">{tenant.slug}</span>
              <span className="flex items-center gap-1">
                <Globe className="h-3 w-3" aria-hidden />
                {tenant.defaultLocale.toUpperCase()} · {tenant.baseCurrencyCode}
              </span>
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" aria-hidden />
                {t('detail.createdAt', {
                  defaultValue: 'Created {{date}}',
                  date: formatDate(tenant.createdAt, { locale: i18n.language }),
                })}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <ShButton variant="outline" onClick={() => setEditOpen(true)}>
            <Edit3 className="h-4 w-4" />
            {t('detail.actions.edit')}
          </ShButton>
          {tenant.status === 'active' ? (
            <ShButton variant="outline" onClick={() => setSuspendOpen(true)}>
              <PauseCircle className="h-4 w-4" />
              {t('detail.actions.suspend')}
            </ShButton>
          ) : tenant.status === 'suspended' ? (
            <ShButton variant="outline" onClick={doReactivate} disabled={busy}>
              <Play className="h-4 w-4" />
              {t('detail.actions.reactivate')}
            </ShButton>
          ) : null}
          <ShButton variant="outline" onClick={() => setPaymentOpen(true)}>
            <CreditCard className="h-4 w-4" />
            {t('detail.actions.recordPayment')}
          </ShButton>
          <ShButton variant="outline" onClick={() => setResetOpen(true)}>
            <KeyRound className="h-4 w-4" />
            {t('detail.actions.resetPassword', { defaultValue: 'Reset password' })}
          </ShButton>
          <Button onClick={() => setImpersonateOpen(true)}>
            <UserCog className="h-4 w-4" />
            {t('detail.actions.impersonate')}
          </Button>
        </div>
      </header>

      {error && (
        <p
          className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          role="alert"
        >
          {error}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiCard
          tone="brand"
          icon={Users}
          label={t('detail.stats.users')}
          value={String(data.stats.userCount)}
        />
        <KpiCard
          tone="info"
          icon={ClipboardList}
          label={t('detail.stats.plans')}
          value={String(data.stats.planCount)}
        />
        <KpiCard
          tone="success"
          icon={ReceiptText}
          label={t('detail.stats.revenue30d')}
          value={fmtMoney(Number(new Decimal(data.stats.revenue30d || 0).toFixed(0)))}
        />
      </div>

      <BillingCard
        tenant={tenant}
        baseCurrency={tenant.baseCurrencyCode}
        onEdit={() => setBillingOpen(true)}
        onPay={() => setPaymentOpen(true)}
      />

      <DomainsCard tenantId={tenant.id} />

      <ModulesCard tenantId={tenant.id} />

      <Card className="p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
          {t('detail.recentAudit')}
        </h2>
        {audit.length === 0 ? (
          <p className="mt-3 text-sm text-fg-muted">{t('audit.empty')}</p>
        ) : (
          <ol className="relative mt-4 ms-3 border-s-2 border-border-subtle">
            {audit.map((a) => (
              <li key={a.id} className="ms-4 pb-4 last:pb-0">
                <span
                  className="absolute -start-[7px] mt-1.5 h-3 w-3 rounded-full bg-warning ring-2 ring-bg"
                  aria-hidden
                />
                <div className="flex items-baseline justify-between gap-2">
                  <code className="rounded bg-bg-subtle px-2 py-0.5 font-mono text-xs">
                    {a.action}
                  </code>
                  <span className="text-xs text-fg-muted">
                    {formatDateTime(a.createdAt, { locale: i18n.language })}
                  </span>
                </div>
                {a.diff !== null && a.diff !== undefined && <AuditDiff diff={a.diff} />}
              </li>
            ))}
          </ol>
        )}
        <Separator className="my-4" />
        <Link
          to={`/platform/audit?tenantId=${tenant.id}`}
          className="inline-flex items-center gap-1 text-xs text-brand underline-offset-2 hover:underline"
        >
          {t('detail.viewAllAudit', { defaultValue: 'View full audit log for this tenant' })}
          <ExternalLink className="h-3 w-3" />
        </Link>
      </Card>

      {/* --- Edit dialog --- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.edit.title')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField id="edit-name" label={t('detail.edit.name')}>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </FormField>
            <FormField id="edit-plan" label={t('detail.edit.plan')}>
              <select
                id="edit-plan"
                className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                value={editForm.plan}
                onChange={(e) => setEditForm((f) => ({ ...f, plan: e.target.value as Tenant['plan'] }))}
              >
                <option value="free">free</option>
                <option value="starter">starter</option>
                <option value="pro">pro</option>
                <option value="enterprise">enterprise</option>
              </select>
            </FormField>
            <FormField id="edit-locale" label={t('detail.edit.defaultLocale')}>
              <select
                id="edit-locale"
                className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                value={editForm.defaultLocale}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, defaultLocale: e.target.value as Tenant['defaultLocale'] }))
                }
              >
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </FormField>
            <FormField id="edit-currency" label={t('detail.edit.baseCurrencyCode')}>
              <Input
                id="edit-currency"
                value={editForm.baseCurrencyCode}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, baseCurrencyCode: e.target.value.toUpperCase() }))
                }
                maxLength={3}
              />
            </FormField>
            <div className="md:col-span-2">
              <LogoUpload
                label={t('detail.edit.logoUrl')}
                value={editForm.logoUrl || null}
                onChange={(next) => setEditForm((f) => ({ ...f, logoUrl: next ?? '' }))}
              />
            </div>
          </div>
          <DialogFooter>
            <ShButton variant="outline" onClick={() => setEditOpen(false)}>
              {t('detail.actions.cancel')}
            </ShButton>
            <Button onClick={saveEdit} loading={busy}>
              {t('detail.actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Suspend dialog --- */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.suspend.title')}</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-fg">
            <Coins className="me-2 inline h-4 w-4 text-warning" aria-hidden />
            {t('detail.suspend.warning', {
              defaultValue:
                'Suspended tenants cannot sign in. Data stays intact and the action is audit-logged.',
            })}
          </div>
          <FormField id="suspend-reason" label={t('detail.suspend.reason')}>
            <Input
              id="suspend-reason"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              maxLength={500}
              placeholder={t('detail.suspend.reasonPlaceholder', {
                defaultValue: 'Non-payment, ToS violation, customer request…',
              })}
            />
          </FormField>
          <DialogFooter>
            <ShButton variant="outline" onClick={() => setSuspendOpen(false)}>
              {t('detail.actions.cancel')}
            </ShButton>
            <Button variant="danger" onClick={doSuspend} loading={busy}>
              {t('detail.suspend.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Reset owner password dialog --- */}
      <Dialog
        open={resetOpen}
        onOpenChange={(o) => {
          setResetOpen(o);
          if (!o) {
            setResetResult(null);
            setResetPwInput('');
            setResetError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.resetPw.title', { defaultValue: 'Reset login password' })}</DialogTitle>
          </DialogHeader>
          {resetResult ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-fg-muted">
                {t('detail.resetPw.doneFor', { defaultValue: 'New password for' })}{' '}
                <span className="font-medium text-fg">{resetResult.email}</span>:
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md border border-border bg-bg-subtle px-3 py-2 font-mono text-sm">
                  {resetResult.tempPassword}
                </code>
                <ShButton
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(resetResult.tempPassword);
                    setResetCopied(true);
                    window.setTimeout(() => setResetCopied(false), 1500);
                  }}
                >
                  {resetCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </ShButton>
              </div>
              <p className="text-xs text-warning">
                {t('detail.resetPw.once', {
                  defaultValue:
                    'Copy it now — shown only once. Give it to the tenant; their old sessions were signed out.',
                })}
              </p>
              <DialogFooter>
                <Button onClick={() => setResetOpen(false)}>
                  {t('detail.resetPw.doneBtn', { defaultValue: 'Done' })}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-fg-muted">
                {t('detail.resetPw.hint', {
                  defaultValue:
                    "Reset the tenant owner's login password. Leave blank to generate a strong one.",
                })}
              </p>
              <FormField
                id="reset-pw"
                label={t('detail.resetPw.newLabel', { defaultValue: 'New password (optional)' })}
              >
                <Input
                  id="reset-pw"
                  type="text"
                  autoComplete="off"
                  value={resetPwInput}
                  onChange={(e) => setResetPwInput(e.target.value)}
                  placeholder={t('detail.resetPw.placeholder', {
                    defaultValue: 'Leave blank to auto-generate',
                  })}
                />
              </FormField>
              {resetError && (
                <p className="text-sm text-danger" role="alert">
                  {resetError}
                </p>
              )}
              <DialogFooter>
                <ShButton variant="outline" onClick={() => setResetOpen(false)}>
                  {t('detail.actions.cancel')}
                </ShButton>
                <Button onClick={doResetPassword} loading={busy}>
                  {t('detail.resetPw.confirm', { defaultValue: 'Reset password' })}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- Billing dialog --- */}
      <Dialog open={billingOpen} onOpenChange={setBillingOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('detail.billing.editTitle')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField id="bill-status" label={t('detail.billing.status')}>
              <select
                id="bill-status"
                className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                value={billingForm.billingStatus}
                onChange={(e) =>
                  setBillingForm((f) => ({ ...f, billingStatus: e.target.value as BillingStatus }))
                }
              >
                <option value="trialing">{t('tenants.billing.trialing')}</option>
                <option value="active">{t('tenants.billing.active')}</option>
                <option value="past_due">{t('tenants.billing.past_due')}</option>
                <option value="unpaid">{t('tenants.billing.unpaid')}</option>
                <option value="cancelled">{t('tenants.billing.cancelled')}</option>
              </select>
            </FormField>
            <FormField id="bill-price" label={t('detail.billing.yearlyPrice')}>
              <div className="flex gap-2">
                <Input
                  id="bill-price"
                  value={billingForm.yearlyPriceAmount}
                  onChange={(e) =>
                    setBillingForm((f) => ({ ...f, yearlyPriceAmount: e.target.value }))
                  }
                  inputMode="decimal"
                  placeholder="588.00"
                  className="flex-1"
                />
                <Input
                  value={billingForm.yearlyPriceCurrencyCode}
                  onChange={(e) =>
                    setBillingForm((f) => ({
                      ...f,
                      yearlyPriceCurrencyCode: e.target.value.toUpperCase(),
                    }))
                  }
                  maxLength={3}
                  className="w-20"
                />
              </div>
            </FormField>
            <FormField id="bill-period-end" label={t('detail.billing.periodEnd')}>
              <Input
                id="bill-period-end"
                type="date"
                value={billingForm.currentPeriodEnd}
                onChange={(e) =>
                  setBillingForm((f) => ({ ...f, currentPeriodEnd: e.target.value }))
                }
              />
            </FormField>
            <FormField id="bill-trial-end" label={t('detail.billing.trialEnd')}>
              <Input
                id="bill-trial-end"
                type="date"
                value={billingForm.trialEndsAt}
                onChange={(e) => setBillingForm((f) => ({ ...f, trialEndsAt: e.target.value }))}
              />
            </FormField>
            <div className="md:col-span-2">
              <FormField id="bill-notes" label={t('detail.billing.notes')}>
                <textarea
                  id="bill-notes"
                  className="block min-h-[80px] w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
                  value={billingForm.billingNotes}
                  onChange={(e) =>
                    setBillingForm((f) => ({ ...f, billingNotes: e.target.value }))
                  }
                  maxLength={2000}
                />
                <p className="mt-1 text-xs text-fg-muted">{t('detail.billing.notesHint')}</p>
              </FormField>
            </div>
          </div>
          <DialogFooter>
            <ShButton variant="outline" onClick={() => setBillingOpen(false)}>
              {t('detail.actions.cancel')}
            </ShButton>
            <Button onClick={saveBilling} loading={busy}>
              {t('detail.actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Record payment dialog --- */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.payment.title')}</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-success/40 bg-success/10 p-3 text-xs text-fg">
            <CheckCircle2 className="me-2 inline h-4 w-4 text-success" aria-hidden />
            {t('detail.payment.hint', {
              defaultValue:
                'Records payment, flips billing status to "Paid", and extends the current period.',
            })}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_2fr]">
            <FormField id="pay-years" label={t('detail.payment.years')}>
              <Input
                id="pay-years"
                type="number"
                min={1}
                max={10}
                value={paymentForm.years}
                onChange={(e) =>
                  setPaymentForm((f) => ({ ...f, years: Number(e.target.value) || 1 }))
                }
              />
            </FormField>
            <FormField id="pay-note" label={t('detail.payment.note')}>
              <Input
                id="pay-note"
                value={paymentForm.note}
                onChange={(e) => setPaymentForm((f) => ({ ...f, note: e.target.value }))}
                placeholder={t('detail.payment.notePlaceholder', {
                  defaultValue: 'wire ref / cheque # / "paid in cash"',
                })}
                maxLength={500}
              />
            </FormField>
          </div>
          {tenant.currentPeriodEnd && (
            <p className="text-xs text-fg-muted">
              {t('detail.payment.preview', {
                defaultValue: 'Period extends from {{from}} to {{to}}.',
                from: formatDate(tenant.currentPeriodEnd, { locale: i18n.language }),
                to: (() => {
                  const anchor =
                    tenant.currentPeriodEnd && new Date(tenant.currentPeriodEnd) > new Date()
                      ? new Date(tenant.currentPeriodEnd)
                      : new Date();
                  const end = new Date(anchor);
                  end.setUTCFullYear(end.getUTCFullYear() + paymentForm.years);
                  return formatDate(end, { locale: i18n.language });
                })(),
              })}
            </p>
          )}
          <DialogFooter>
            <ShButton variant="outline" onClick={() => setPaymentOpen(false)}>
              {t('detail.actions.cancel')}
            </ShButton>
            <Button onClick={recordPayment} loading={busy}>
              {t('detail.actions.recordPayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Impersonate dialog --- */}
      <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.impersonate.title')}</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-fg">
            <UserCog className="me-2 inline h-4 w-4 text-danger" aria-hidden />
            {t('detail.impersonate.warning', {
              defaultValue:
                'Impersonation is audit-logged with the reason you provide. You will see and act as the tenant owner.',
            })}
          </div>
          <FormField id="imp-reason" label={t('detail.impersonate.reason')} required>
            <Input
              id="imp-reason"
              value={impersonateReason}
              onChange={(e) => setImpersonateReason(e.target.value)}
              minLength={1}
              maxLength={500}
              required
              placeholder={t('detail.impersonate.reasonPlaceholder', {
                defaultValue: 'Customer support ticket #1234',
              })}
            />
          </FormField>
          <DialogFooter>
            <ShButton variant="outline" onClick={() => setImpersonateOpen(false)}>
              {t('detail.actions.cancel')}
            </ShButton>
            <Button
              variant="danger"
              onClick={doImpersonate}
              loading={busy}
              disabled={impersonateReason.trim().length === 0}
            >
              {t('detail.impersonate.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Billing card

interface BillingCardProps {
  tenant: Tenant;
  baseCurrency: string;
  onEdit: () => void;
  onPay: () => void;
}

function BillingCard({ tenant, baseCurrency, onEdit, onPay }: BillingCardProps): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const expiresAt = tenant.currentPeriodEnd ?? tenant.trialEndsAt;
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const daysLeft = expiresDate
    ? Math.ceil((expiresDate.getTime() - Date.now()) / 86_400_000)
    : null;
  const overdue = daysLeft !== null && daysLeft < 0;
  const warning = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

  const priceCurrency = tenant.yearlyPriceCurrencyCode ?? baseCurrency;
  const formattedPrice = (() => {
    if (!tenant.yearlyPriceAmount) return null;
    try {
      return narrowUsd(formatNumber(Number(tenant.yearlyPriceAmount), { locale: i18n.language }, {
        style: 'currency',
        currency: priceCurrency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: 2,
      }));
    } catch {
      return `${tenant.yearlyPriceAmount} ${priceCurrency}`;
    }
  })();

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-info/15 text-info">
            <CreditCard className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('detail.billing.title')}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge variant={BILLING_VARIANT[tenant.billingStatus]}>
                {t(`tenants.billing.${tenant.billingStatus}`)}
              </Badge>
              {formattedPrice && (
                <span className="font-mono text-sm font-medium tabular-nums">
                  {formattedPrice}
                  <span className="text-xs text-fg-muted"> / yr</span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <ShButton variant="outline" size="sm" onClick={onEdit}>
            <Edit3 className="h-4 w-4" />
            {t('detail.actions.edit')}
          </ShButton>
          <ShButton size="sm" onClick={onPay}>
            <CreditCard className="h-4 w-4" />
            {t('detail.actions.recordPayment')}
          </ShButton>
        </div>
      </div>

      <Separator className="my-4" />

      <dl className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
        <BillingField
          icon={<CalendarClock className="h-4 w-4" aria-hidden />}
          label={t('detail.billing.periodEnd')}
          value={expiresDate ? formatDate(expiresDate, { locale: i18n.language }) : '—'}
          hint={
            daysLeft === null
              ? undefined
              : overdue
                ? t('detail.billing.overdue', {
                    days: -daysLeft,
                    defaultValue: '{{days}} days overdue',
                  })
                : t('detail.billing.daysLeft', {
                    days: daysLeft,
                    defaultValue: '{{days}} days left',
                  })
          }
          hintTone={overdue ? 'danger' : warning ? 'warning' : 'muted'}
        />
        <BillingField
          icon={<CalendarDays className="h-4 w-4" aria-hidden />}
          label={t('detail.billing.lastPayment')}
          value={
            tenant.lastPaymentAt ? (
              formatDate(tenant.lastPaymentAt, { locale: i18n.language })
            ) : (
              <span className="italic text-fg-subtle">{t('detail.billing.neverPaid')}</span>
            )
          }
        />
        <BillingField
          icon={<Coins className="h-4 w-4" aria-hidden />}
          label={t('detail.billing.trialEnd')}
          value={
            tenant.trialEndsAt
              ? formatDate(tenant.trialEndsAt, { locale: i18n.language })
              : '—'
          }
        />
      </dl>

      {tenant.billingNotes && (
        <div className="mt-4 rounded-md border border-border-subtle bg-bg-subtle/40 p-3 text-xs">
          <div className="mb-1 font-semibold uppercase tracking-wide text-fg-muted">
            {t('detail.billing.notes')}
          </div>
          <pre className="whitespace-pre-wrap font-sans text-xs text-fg">{tenant.billingNotes}</pre>
        </div>
      )}

      {(overdue || tenant.billingStatus === 'past_due' || tenant.billingStatus === 'unpaid') && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-warning" aria-hidden />
          <span>{t('detail.billing.warning', { defaultValue: 'This tenant is behind on payment. Send a reminder or suspend the account if the situation continues.' })}</span>
        </div>
      )}
    </Card>
  );
}

interface BillingFieldProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  hintTone?: 'muted' | 'warning' | 'danger';
}

function BillingField({ icon, label, value, hint, hintTone = 'muted' }: BillingFieldProps): JSX.Element {
  const toneClass =
    hintTone === 'danger'
      ? 'text-danger'
      : hintTone === 'warning'
        ? 'text-warning'
        : 'text-fg-muted';
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-fg-muted">{icon}</span>
      <div>
        <div className="text-[0.7rem] uppercase tracking-wide text-fg-muted">{label}</div>
        <div className="mt-0.5 font-medium">{value}</div>
        {hint && <div className={`mt-0.5 text-xs ${toneClass}`}>{hint}</div>}
      </div>
    </div>
  );
}

const DOMAIN_STATUS_VARIANT: Record<TenantDomain['status'], 'success' | 'warning' | 'secondary'> = {
  verified: 'success',
  pending: 'warning',
  disabled: 'secondary',
};

/**
 * Per-tenant module on/off management for the platform admin. Reads the
 * tenant's module map, lets the operator toggle each optional module, and
 * saves it (settings.modules in the tenant's own DB). Self-contained.
 */
function ModulesCard({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useTranslation('platform');
  const { t: tm } = useTranslation('settings');
  const { t: tc } = useTranslation();
  const [flags, setFlags] = useState<Record<ModuleKey, boolean> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await platformApi().get<{ modules: Record<string, boolean> }>(
        `/tenants/${tenantId}/modules`,
      );
      const map = r.data.modules ?? {};
      const next = {} as Record<ModuleKey, boolean>;
      for (const m of TOGGLEABLE_MODULES) next[m] = map[m] !== false;
      setFlags(next);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = async (): Promise<void> => {
    if (!flags) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await platformApi().put(`/tenants/${tenantId}/modules`, { modules: flags });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
          <Boxes className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
            {t('modules.title', { defaultValue: 'Modules' })}
          </h2>
          <p className="mt-1 text-xs text-fg-muted">
            {t('modules.subtitle', {
              defaultValue:
                'Choose which modules this tenant may use. Unchecked modules are locked off — the tenant cannot re-enable them from their own settings.',
            })}
          </p>
        </div>
      </div>

      <Separator className="my-4" />

      {error && (
        <p className="mb-3 text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      {!flags ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            {TOGGLEABLE_MODULES.map((m) => (
              <label
                key={m}
                className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-bg-subtle"
              >
                <span className="min-w-0 truncate text-sm text-fg">
                  {tm(`modules.items.${m}.label`, { defaultValue: m })}
                </span>
                <input
                  type="checkbox"
                  className="h-5 w-5 flex-none accent-brand"
                  checked={flags[m]}
                  onChange={(e) => setFlags((f) => (f ? { ...f, [m]: e.target.checked } : f))}
                  aria-label={tm(`modules.items.${m}.label`, { defaultValue: m })}
                />
              </label>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={onSave} loading={busy}>
              {tc('actions.save')}
            </Button>
            {saved && (
              <span className="text-sm text-success">
                {t('modules.saved', { defaultValue: 'Saved.' })}
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Custom-domain management for a tenant. Lists domains with their verification
 * status, shows the DNS records to publish, and lets the operator add / verify /
 * remove a domain. Self-contained: owns its own state + reloads after each call.
 */
function DomainsCard({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useTranslation('platform');
  const [domains, setDomains] = useState<TenantDomain[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<{ hostname: string; kind: 'app' | 'storefront' }>({
    hostname: '',
    kind: 'storefront',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await platformApi().get<{ items: TenantDomain[] }>(`/tenants/${tenantId}/domains`);
      setDomains(r.data.items);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoaded(true);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const copy = (key: string, value: string): void => {
    void navigator.clipboard.writeText(value);
    setCopied(key);
    window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
  };

  const onAdd = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await platformApi().post(`/tenants/${tenantId}/domains`, {
        hostname: form.hostname.trim().toLowerCase(),
        kind: form.kind,
      });
      setAddOpen(false);
      setForm({ hostname: '', kind: 'storefront' });
      await load();
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(
        status === 409
          ? t('domains.errorTaken')
          : status === 400 || status === 422
            ? t('domains.errorInvalid')
            : String((err as Error).message ?? err),
      );
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (d: TenantDomain): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await platformApi().post(`/tenants/${tenantId}/domains/${d.id}/verify`, {});
      await load();
    } catch {
      setError(t('domains.verifyFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (d: TenantDomain): Promise<void> => {
    if (!(await confirmDialog({ message: t('domains.removeConfirm', { hostname: d.hostname }), tone: 'danger' }))) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await platformApi().delete(`/tenants/${tenantId}/domains/${d.id}`);
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-info/15 text-info">
            <Globe className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('domains.title')}
            </h2>
            <p className="mt-1 text-xs text-fg-muted">{t('domains.subtitle')}</p>
          </div>
        </div>
        <ShButton size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('domains.add')}
        </ShButton>
      </div>

      <Separator className="my-4" />

      {error && (
        <p className="mb-3 text-sm text-danger" role="alert">
          {error}
        </p>
      )}

      {!loaded ? (
        <Skeleton className="h-16 w-full" />
      ) : domains.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('domains.empty')}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {domains.map((d) => (
            <li key={d.id} className="flex flex-col gap-3 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-mono text-sm">{d.hostname}</span>
                  <Badge variant="secondary">{t(`domains.kindOption.${d.kind}`)}</Badge>
                  <Badge variant={DOMAIN_STATUS_VARIANT[d.status]}>{t(`domains.status.${d.status}`)}</Badge>
                  {d.isPrimary && <Badge variant="secondary">{t('domains.primary')}</Badge>}
                </div>
                <div className="flex gap-2">
                  {d.status !== 'verified' && (
                    <ShButton size="sm" variant="outline" onClick={() => void onVerify(d)} disabled={busy}>
                      <RefreshCw className="h-4 w-4" />
                      {t('domains.verify')}
                    </ShButton>
                  )}
                  <ShButton size="sm" variant="outline" onClick={() => void onRemove(d)} disabled={busy}>
                    <Trash2 className="h-4 w-4" />
                    {t('domains.remove')}
                  </ShButton>
                </div>
              </div>

              {d.status === 'pending' && (
                <div className="rounded-md border border-border-subtle bg-bg-subtle/40 p-3 text-xs">
                  <div className="mb-2 font-semibold text-fg">{t('domains.dnsInstructions')}</div>
                  <DnsRow
                    label={t('domains.txtName', { defaultValue: 'TXT name' })}
                    value={`_erp-verify.${d.hostname}`}
                    copied={copied === `${d.id}:name`}
                    onCopy={() => copy(`${d.id}:name`, `_erp-verify.${d.hostname}`)}
                    copyLabel={t('domains.copy')}
                    copiedLabel={t('domains.copied')}
                  />
                  <DnsRow
                    label={t('domains.txtValue', { defaultValue: 'TXT value' })}
                    value={d.verificationToken}
                    copied={copied === `${d.id}:token`}
                    onCopy={() => copy(`${d.id}:token`, d.verificationToken)}
                    copyLabel={t('domains.copy')}
                    copiedLabel={t('domains.copied')}
                  />
                  <p className="mt-2 text-fg-muted">{t('domains.targetRecord')}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('domains.add')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onAdd} className="flex flex-col gap-4" noValidate>
            <FormField id="domain-host" label={t('domains.hostname')} required>
              <Input
                id="domain-host"
                value={form.hostname}
                placeholder="shop.acme.com"
                autoCapitalize="none"
                onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
              />
            </FormField>
            <FormField id="domain-kind" label={t('domains.kind')}>
              <select
                id="domain-kind"
                className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as 'app' | 'storefront' }))}
              >
                <option value="storefront">{t('domains.kindOption.storefront')}</option>
                <option value="app">{t('domains.kindOption.app')}</option>
              </select>
            </FormField>
            <DialogFooter>
              <ShButton type="button" variant="outline" onClick={() => setAddOpen(false)}>
                {t('detail.actions.cancel')}
              </ShButton>
              <Button type="submit" loading={busy}>
                {t('domains.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface DnsRowProps {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  copyLabel: string;
  copiedLabel: string;
}

function DnsRow({ label, value, copied, onCopy, copyLabel, copiedLabel }: DnsRowProps): JSX.Element {
  return (
    <div className="mt-1 flex items-center gap-2">
      <span className="w-20 shrink-0 text-fg-muted">{label}</span>
      <code className="min-w-0 flex-1 select-all truncate rounded border border-border bg-bg px-2 py-1 font-mono">
        {value}
      </code>
      <ShButton type="button" size="sm" variant="outline" onClick={onCopy}>
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5" />
            {copiedLabel}
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" />
            {copyLabel}
          </>
        )}
      </ShButton>
    </div>
  );
}

